import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ACTIVE_SCENARIO_RUN_STATUSES, scenarioRunsApi } from '@/lib/api/scenario-runs.api';

import type { CreateScenarioRunInput } from '@/lib/api/scenario-runs.api';

const QK = ['scenario-runs'];
const GATE_QK = ['scenario-gate'];
const ASSIGNMENTS_QK = ['scenario-assignments'];

const POLL_MS = 5000;

export const useScenarioRuns = (configurationId?: string) =>
  useQuery({
    queryKey: [...QK, configurationId ?? 'all'],
    queryFn: () => scenarioRunsApi.list(configurationId),
    refetchInterval: (q) =>
      q.state.data?.some((r) => ACTIVE_SCENARIO_RUN_STATUSES.has(r.status)) ? POLL_MS : false,
  });

export const useScenarioRun = (id: string | undefined) =>
  useQuery({
    queryKey: [...QK, 'one', id],
    queryFn: () => scenarioRunsApi.get(id!),
    enabled: !!id,
    refetchInterval: (q) =>
      q.state.data && ACTIVE_SCENARIO_RUN_STATUSES.has(q.state.data.status) ? POLL_MS : false,
  });

export const useCreateScenarioRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateScenarioRunInput) => scenarioRunsApi.create(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
      void qc.invalidateQueries({ queryKey: GATE_QK });
    },
  });
};

export const useCancelScenarioRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scenarioRunsApi.cancel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
};

export const useRerunFailedScenarios = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scenarioRunsApi.rerunFailed(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
};

export const useAdjustPromptFromScenarioRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scenarioRunsApi.adjust(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
      void qc.invalidateQueries({ queryKey: ['pipeline-runs'] });
    },
  });
};

export const useScenarioAssignments = (configurationId: string | undefined) =>
  useQuery({
    queryKey: [...ASSIGNMENTS_QK, configurationId],
    queryFn: () => scenarioRunsApi.assignments(configurationId!),
    enabled: !!configurationId,
  });

export const useScenarioGate = (configurationId: string | undefined, commitId?: string) =>
  useQuery({
    queryKey: [...GATE_QK, configurationId, commitId ?? 'head'],
    queryFn: () => scenarioRunsApi.gate(configurationId!, commitId),
    enabled: !!configurationId,
  });
